import type { SaveExperienceInput } from 'sr-types/lib/person/v1/graphql';
import type { search } from 'sr-types';
import { AllPermissions } from '../../common/auth/permissions';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import type { Reference } from '../../common/reference/reference';

type ReferenceValue = {
    id?: string | null;
    label?: string | null;
    type?: string | null;
    uriPrefix?: string | null;
    eventId?: string | null;
    deleted?: boolean | null;
};

export type OrganizationReferenceValue = ReferenceValue & {
    name?: string | null;
    ref?: ReferenceValue | null;
    reference?: ReferenceValue | null;
};

export type SlimPersonRepresentativeFields = Pick<search.Person, 'organization' | 'organizationReference' | 'role'>;
type SlimPersonSecurityFields = Pick<search.Person, 'security'>;

export type RepresentativeDefaults = {
    canEditProfile?: boolean;
    organizationReference?: Reference;
    role?: string;
};

type BuildRepresentativeExperienceInputParams = {
    organizationReference?: Reference;
    role?: string;
    existingRepresentativeFields?: RepresentativeDefaults;
};

const toOptionalString = (value?: string | null): string | undefined => {
    const trimmedValue = value?.trim();
    return trimmedValue || undefined;
};

const toOptionalBoolean = (value?: boolean | null): boolean | undefined => (value === null ? undefined : value);

export const getOrganizationReference = (value?: OrganizationReferenceValue | null): Reference | undefined => {
    const reference = value?.ref || value?.reference || value;

    if (!reference?.id) {
        return undefined;
    }

    const label = toOptionalString(reference.label) || toOptionalString(value?.label) || toOptionalString(value?.name);

    return {
        id: reference.id,
        label,
        type: toOptionalString(reference.type) || ENTITY_CONSTANTS.organization,
        uriPrefix: toOptionalString(reference.uriPrefix),
        eventId: toOptionalString(reference.eventId),
        deleted: toOptionalBoolean(reference.deleted)
    };
};

export const fillOrganizationReferenceLabel = (
    organizationReference?: Reference,
    fallbackOrganizationReference?: Reference
): Reference | undefined => {
    if (
        !organizationReference?.id ||
        organizationReference.label ||
        organizationReference.id !== fallbackOrganizationReference?.id
    ) {
        return organizationReference;
    }

    return {
        ...organizationReference,
        label: fallbackOrganizationReference.label
    };
};

export const getSlimRepresentativeFields = (
    person?: (SlimPersonRepresentativeFields & SlimPersonSecurityFields) | null
): RepresentativeDefaults => {
    const organizationReference = getOrganizationReference({
        ...(person?.organizationReference || {}),
        label: toOptionalString(person?.organizationReference?.label) || toOptionalString(person?.organization),
        name: toOptionalString(person?.organization)
    });

    return {
        canEditProfile: person?.security?.allowedActions?.includes(AllPermissions.Edit) ?? false,
        organizationReference,
        role: toOptionalString(person?.role)
    };
};

export const buildRepresentativeExperienceInput = ({
    organizationReference,
    role,
    existingRepresentativeFields = {}
}: BuildRepresentativeExperienceInputParams): SaveExperienceInput | undefined => {
    const existingExperienceInput =
        existingRepresentativeFields.organizationReference?.id || existingRepresentativeFields.role
            ? {
                  experienceName: existingRepresentativeFields.organizationReference?.label || '',
                  organizationReference: existingRepresentativeFields.organizationReference,
                  professionalRoles: existingRepresentativeFields.role ? [existingRepresentativeFields.role] : []
              }
            : undefined;
    const existingOrganizationId = existingRepresentativeFields.organizationReference?.id;
    const resolvedRole = role || '';
    const existingRole = existingRepresentativeFields.role || '';
    const isOrganizationChanged =
        Boolean(organizationReference?.id) && existingOrganizationId !== organizationReference.id;
    const isRoleChanged = Boolean(resolvedRole) && resolvedRole !== existingRole;

    if (!isOrganizationChanged && !isRoleChanged) {
        return undefined;
    }

    return {
        existing: existingExperienceInput,
        forceUserChange: true,
        new: {
            ...(existingExperienceInput || {}),
            ...(organizationReference?.id
                ? {
                      experienceName: organizationReference.label || existingExperienceInput?.experienceName || '',
                      organizationReference
                  }
                : {}),
            professionalRoles: resolvedRole ? [resolvedRole] : existingExperienceInput?.professionalRoles || []
        }
    };
};

type PrefillBusinessContact = {
    name?: unknown;
    contactInfo?: unknown;
    role?: string | null;
    isPrimary?: boolean | null;
    organization?: string | null;
    organizationReference?: OrganizationReferenceValue | null;
    personReference?: ReferenceValue | null;
    profileImageReference?: unknown;
};

/** Line item itemInfo keeps the entity type on `ref`; form fields holding a plain reference carry it directly. */
export const getItemPersonReference = (
    value?: (ReferenceValue & { ref?: ReferenceValue | null }) | null
): ReferenceValue | null | undefined => (value?.ref?.id ? value.ref : value);

/** Index of the single primary contact: the first one flagged primary, or -1 when none is flagged. */
export const getPrimaryContactIndex = (contacts: Array<{ isPrimary?: boolean | null }>): number => {
    const primaryContactIndex = contacts.findIndex((contact) => contact.isPrimary === true);
    return primaryContactIndex;
};

export const getBusinessContactOrganizationReference = (businessContact: PrefillBusinessContact) =>
    getOrganizationReference({
        ...(businessContact.organizationReference || {}),
        label: businessContact.organizationReference?.label || businessContact.organization
    });

/** The slim-search lookup is only needed when the contact lacks organization or role, and has a person to look up. */
export const shouldFetchRepresentativeDefaults = (businessContact: PrefillBusinessContact): boolean => {
    const organizationReference = getBusinessContactOrganizationReference(businessContact);
    const hasCompleteFields = Boolean(
        organizationReference?.id && organizationReference?.label && businessContact.role
    );

    return !hasCompleteFields && Boolean(businessContact.personReference?.id);
};

export const buildPrefilledRepresentative = (
    businessContact: PrefillBusinessContact,
    defaults: RepresentativeDefaults,
    isPrimary: boolean
) => {
    const organizationReference =
        fillOrganizationReferenceLabel(
            getBusinessContactOrganizationReference(businessContact),
            defaults.organizationReference
        ) || defaults.organizationReference;

    return {
        name: businessContact.name,
        contactInfo: businessContact.contactInfo,
        role: businessContact.role || defaults.role,
        isPrimary,
        personReference: businessContact.personReference,
        profileImageReference: businessContact.profileImageReference,
        organizationReference,
        organization: organizationReference?.label
    };
};
